import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  X,
  Plus,
  Calendar,
  Clock,
  User,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Tag,
  Link,
  Paperclip,
  MessageSquare,
  CheckCircle
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const CreateTask = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { success, error: showError } = useToast();
  
  // Get projectId from URL params or query string (optional)
  const preSelectedProjectId = projectId || searchParams.get('projectId');
  
  // Tab management
  const [activeTab, setActiveTab] = useState('basic');
  
  // Projects and tasks for dropdowns
  const [projects, setProjects] = useState([]);
  const [parentTasks, setParentTasks] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    title: '',
    description: '',
    project: preSelectedProjectId || '',
    parentTask: '',
    status: 'To Do',
    priority: 'Medium',
    assignee: '',
    createdBy: '',
    
    // Timeline & Effort
    dueDate: '',
    startDate: '',
    estimatedHours: '',
    actualHours: '',
    progress: 0,
    
    // Task Details
    tags: [],
    dependencies: [],
    comments: [],
    attachments: [],
    
    // Advanced Settings
    isActive: true,
    customFields: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (formData.project) {
      loadParentTasks(formData.project);
    } else {
      setParentTasks([]);
    }
  }, [formData.project]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}?limit=1000&isActive=true`, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const data = await response.json();
        const projectsList = data.data?.projects || data.projects || [];
        setProjects(projectsList);
      } else {
        throw new Error('Failed to load projects');
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      showError('Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadParentTasks = async (projectId) => {
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.TASKS}/project/${projectId}?parentTask=null`, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const data = await response.json();
        const tasksList = data.data?.tasks || data.tasks || [];
        setParentTasks(tasksList);
      }
    } catch (err) {
      console.error('Error loading parent tasks:', err);
    }
  };

  // Tab configuration
  const tabs = [
    {
      id: 'basic',
      label: 'Task Setup',
      icon: <FileText size={18} />
    },
    {
      id: 'timeline',
      label: 'Timeline & Effort',
      icon: <Clock size={18} />
    },
    {
      id: 'details',
      label: 'Task Details',
      icon: <Target size={18} />
    },
    {
      id: 'advanced',
      label: 'Advanced Settings',
      icon: <Settings size={18} />
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const handleArrayRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleDependencyChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev.dependencies.map((dep, i) => 
        i === index ? { ...dep, [field]: value } : dep
      )
    }));
  };

  const handleAddDependency = () => {
    const newDependency = {
      task: '',
      type: 'Blocks',
      description: ''
    };
    
    setFormData(prev => ({
      ...prev,
      dependencies: [...prev.dependencies, newDependency]
    }));
  };

  const handleRemoveDependency = (index) => {
    setFormData(prev => ({
      ...prev,
      dependencies: prev.dependencies.filter((_, i) => i !== index)
    }));
  };

  const handleCommentChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      comments: prev.comments.map((comment, i) => 
        i === index ? { ...comment, [field]: value } : comment
      )
    }));
  };

  const handleAddComment = () => {
    const newComment = {
      text: '',
      author: '',
      createdAt: new Date().toISOString()
    };
    
    setFormData(prev => ({
      ...prev,
      comments: [...prev.comments, newComment]
    }));
  };

  const handleRemoveComment = (index) => {
    setFormData(prev => ({
      ...prev,
      comments: prev.comments.filter((_, i) => i !== index)
    }));
  };

  const handleAttachmentChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.map((attachment, i) => 
        i === index ? { ...attachment, [field]: value } : attachment
      )
    }));
  };

  const handleAddAttachment = () => {
    const newAttachment = {
      name: '',
      type: '',
      url: '',
      uploadedBy: '',
      uploadedDate: new Date().toISOString()
    };
    
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, newAttachment]
    }));
  };

  const handleRemoveAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const isObjectId = (str) => /^[0-9a-fA-F]{24}$/.test(str);
    
    try {
      if (!formData.project) {
        showError('Please select a project');
        setIsSubmitting(false);
        return;
      }

      if (!formData.title.trim()) {
        showError('Task title is required');
        setIsSubmitting(false);
        return;
      }

      const desiredStatus = formData.status;

      let safeCreationStatus = "In Progress"; 
      if (desiredStatus === "Cancelled") safeCreationStatus = "Cancelled";
      if (desiredStatus === "In Progress") safeCreationStatus = "In Progress";

      const taskData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        project: formData.project,
        status: safeCreationStatus, // Send the "Trojan Horse" status
        priority: formData.priority,
        isActive: formData.isActive
      };

      // Handle Parent Task (Strict)
      if (formData.parentTask && isObjectId(formData.parentTask)) {
        taskData.parentTask = formData.parentTask;
      }

      // Handle Assignee
      const assigneeVal = formData.assignee ? formData.assignee.trim() : '';
      if (assigneeVal) {
        if (isObjectId(assigneeVal)) {
           taskData.assignee = assigneeVal;
        } else {
           taskData.assigneeName = assigneeVal;
        }
      }
      
      // Handle CreatedBy
      const createdByVal = formData.createdBy ? formData.createdBy.trim() : '';
      if (createdByVal) {
        if (isObjectId(createdByVal)) {
          taskData.createdBy = createdByVal;
        } else {
          taskData.createdByName = createdByVal;
        }
      }

      // Optional Data
      if (formData.dueDate) taskData.dueDate = new Date(formData.dueDate);
      if (formData.startDate) taskData.startDate = new Date(formData.startDate);
      if (formData.estimatedHours) taskData.estimatedHours = parseFloat(formData.estimatedHours);
      if (formData.actualHours) taskData.actualHours = parseFloat(formData.actualHours);
      if (formData.progress) taskData.progress = parseFloat(formData.progress);
      
      taskData.tags = Array.isArray(formData.tags) ? formData.tags : [];
      taskData.dependencies = Array.isArray(formData.dependencies) ? formData.dependencies.map(dep => ({
          task: dep.task || null,
          type: dep.type || 'Related'
      })) : [];

      console.log(`Step 1: Creating Task with safe status "${safeCreationStatus}"...`);

      const response = await fetch(API_CONFIG.ENDPOINTS.TASKS, {
        method: 'POST',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(taskData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle Validation Errors
        if (data.errors && data.errors.length > 0) {
          throw new Error(`${data.errors[0].field}: ${data.errors[0].message}`);
        } else {
          throw new Error(data.message || 'Failed to create task');
        }
      }

      const newTaskId = data.data?.task?._id || data.data?._id || data._id;

      if (newTaskId && desiredStatus !== safeCreationStatus) {
         console.log(`Step 2: Correcting status to "${desiredStatus}"...`);
         
         const patchResponse = await fetch(`${API_CONFIG.ENDPOINTS.TASKS}/${newTaskId}/status`, {
            method: 'PATCH',
            headers: API_CONFIG.HEADERS,
            body: JSON.stringify({ status: desiredStatus })
         });

         if (!patchResponse.ok) {
            console.warn("Status correction failed. Task created as In Progress.");
            // We do not stop here, we still consider the creation a success
            // but we might warn the user.
         }
      }

      success('Task created successfully!');
      navigate('/projects/task-management');

    } catch (err) {
      console.error('Error creating task:', err);
      showError(err.message || 'Failed to create task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBasicInformation = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Basic Information</h3>
        <p className="section-description">Task title, description, and basic details</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Task Title *</label>
        <input
          type="text"
          className="form-control"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="Enter task title"
          required
        />
      </div>
      
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          className="form-control"
          rows="4"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Describe the task objectives and requirements..."
        />
      </div>
      
      <div className="form-grid-3">
        <div className="form-group">
          <label className="form-label">Project *</label>
          {loadingProjects ? (
            <div className="form-control" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '38px' }}>
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <select
              className="form-select"
              value={formData.project}
              onChange={(e) => handleInputChange('project', e.target.value)}
              required
            >
              <option value="">Select project</option>
              {projects.map((project) => (
                <option key={project._id || project.id} value={project._id || project.id}>
                  {project.projectCode ? `${project.projectCode} - ` : ''}{project.name}
                </option>
              ))}
            </select>
          )}
          {projects.length === 0 && !loadingProjects && (
            <small className="text-muted">No active projects found. Please create a project first.</small>
          )}
        </div>
        
        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="To Do">To Do</option>
            <option value="In Progress">In Progress</option>
            <option value="Review">Review</option>
            <option value="Done">Done</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="form-group">
          <label className="form-label">Priority</label>
          <select
            className="form-select"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </select>
        </div>
      </div>
      
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Assignee *</label>
          <input
            type="text"
            className="form-control"
            value={formData.assignee}
            onChange={(e) => handleInputChange('assignee', e.target.value)}
            placeholder="Enter assignee name"
            required
          />
          <small className="text-muted">Enter the name of the person assigned to this task</small>
        </div>
        
        <div className="form-group">
          <label className="form-label">Created By</label>
          <input
            type="text"
            className="form-control"
            value={formData.createdBy}
            onChange={(e) => handleInputChange('createdBy', e.target.value)}
            placeholder="Enter creator name"
          />
          <small className="text-muted">Enter the name of the person creating this task</small>
        </div>
      </div>

      {formData.project && (
        <div className="form-group">
          <label className="form-label">Parent Task (Optional)</label>
          <select
            className="form-select"
            value={formData.parentTask}
            onChange={(e) => handleInputChange('parentTask', e.target.value)}
          >
            <option value="">None (This is a parent task)</option>
            {parentTasks.map((task) => (
              <option key={task._id || task.id} value={task._id || task.id}>
                {task.title} {task.status ? `(${task.status})` : ''}
              </option>
            ))}
          </select>
          <small className="text-muted">Select a parent task if this is a subtask</small>
        </div>
      )}
    </div>
  );

  const renderTimelineEffort = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Timeline & Effort</h3>
        <p className="section-description">Schedule, duration, and effort estimation</p>
      </div>
      
      <div className="form-grid-2">
        <div className="form-group">
          <label className="form-label">Start Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Due Date</label>
          <input
            type="date"
            className="form-control"
            value={formData.dueDate}
            onChange={(e) => handleInputChange('dueDate', e.target.value)}
          />
        </div>
      </div>
      
      <div className="form-grid-3">
        <div className="form-group">
          <label className="form-label">Estimated Hours</label>
          <input
            type="number"
            className="form-control"
            value={formData.estimatedHours}
            onChange={(e) => handleInputChange('estimatedHours', e.target.value)}
            placeholder="0"
            min="0"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Actual Hours</label>
          <input
            type="number"
            className="form-control"
            value={formData.actualHours}
            onChange={(e) => handleInputChange('actualHours', e.target.value)}
            placeholder="0"
            min="0"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Progress (%)</label>
          <input
            type="number"
            className="form-control"
            value={formData.progress}
            onChange={(e) => handleInputChange('progress', e.target.value)}
            placeholder="0"
            min="0"
            max="100"
          />
        </div>
      </div>
    </div>
  );

  const renderTaskDetails = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Task Tags</h3>
        <p className="section-description">Add tags to categorize and organize tasks</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Tags</label>
        <div className="array-input-group">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Add tag"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleArrayAdd('tags', e.target.value);
                  e.target.value = '';
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline"
              onClick={(e) => {
                const input = e.target.parentElement.querySelector('input');
                if (input && input.value.trim()) {
                  handleArrayAdd('tags', input.value);
                  input.value = '';
                }
              }}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="array-items">
            {formData.tags.map((tag, index) => (
              <div key={index} className="array-item">
                <span>{tag}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleArrayRemove('tags', index)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Task Dependencies</h3>
        <p className="section-description">Define relationships with other tasks</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Dependencies</label>
        <div className="dependencies-container">
          {formData.dependencies.map((dependency, index) => (
            <div key={index} className="dependency-item">
              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Related Task</label>
                  <input
                    type="text"
                    className="form-control"
                    value={dependency.task}
                    onChange={(e) => handleDependencyChange(index, 'task', e.target.value)}
                    placeholder="Task title or ID"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Dependency Type</label>
                  <select
                    className="form-select"
                    value={dependency.type}
                    onChange={(e) => handleDependencyChange(index, 'type', e.target.value)}
                  >
                    <option value="Blocks">Blocks</option>
                    <option value="Blocked By">Blocked By</option>
                    <option value="Related">Related</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={dependency.description}
                    onChange={(e) => handleDependencyChange(index, 'description', e.target.value)}
                    placeholder="Dependency description"
                  />
                </div>
              </div>
              
              <div className="dependency-actions">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveDependency(index)}
                >
                  <X size={16} />
                  Remove Dependency
                </button>
              </div>
            </div>
          ))}
          
          <div className="add-dependency">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleAddDependency}
            >
              <Plus size={16} />
              Add Dependency
            </button>
          </div>
        </div>
      </div>
      
      <div className="section-header">
        <h3 className="section-title">Comments & Attachments</h3>
        <p className="section-description">Add initial comments and attach files</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Initial Comments</label>
        <div className="comments-container">
          {formData.comments.map((comment, index) => (
            <div key={index} className="comment-item">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Comment</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={comment.text}
                    onChange={(e) => handleCommentChange(index, 'text', e.target.value)}
                    placeholder="Add a comment..."
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Author</label>
                  <input
                    type="text"
                    className="form-control"
                    value={comment.author}
                    onChange={(e) => handleCommentChange(index, 'author', e.target.value)}
                    placeholder="Author name"
                  />
                </div>
              </div>
              
              <div className="comment-actions">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveComment(index)}
                >
                  <X size={16} />
                  Remove Comment
                </button>
              </div>
            </div>
          ))}
          
          <div className="add-comment">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleAddComment}
            >
              <Plus size={16} />
              Add Comment
            </button>
          </div>
        </div>
      </div>
      
      <div className="form-group">
        <label className="form-label">Attachments</label>
        <div className="attachments-container">
          {formData.attachments.map((attachment, index) => (
            <div key={index} className="attachment-item">
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">File Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={attachment.name}
                    onChange={(e) => handleAttachmentChange(index, 'name', e.target.value)}
                    placeholder="File name"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">File Type</label>
                  <select
                    className="form-select"
                    value={attachment.type}
                    onChange={(e) => handleAttachmentChange(index, 'type', e.target.value)}
                  >
                    <option value="">Select type</option>
                    <option value="document">Document</option>
                    <option value="image">Image</option>
                    <option value="code">Code</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">URL</label>
                  <input
                    type="url"
                    className="form-control"
                    value={attachment.url}
                    onChange={(e) => handleAttachmentChange(index, 'url', e.target.value)}
                    placeholder="File URL"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Uploaded By</label>
                  <input
                    type="text"
                    className="form-control"
                    value={attachment.uploadedBy}
                    onChange={(e) => handleAttachmentChange(index, 'uploadedBy', e.target.value)}
                    placeholder="Uploader name"
                  />
                </div>
              </div>
              
              <div className="attachment-actions">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveAttachment(index)}
                >
                  <X size={16} />
                  Remove Attachment
                </button>
              </div>
            </div>
          ))}
          
          <div className="add-attachment">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleAddAttachment}
            >
              <Plus size={16} />
              Add Attachment
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAdvancedSettings = () => (
    <div className="task-form-section">
      <div className="section-header">
        <h3 className="section-title">Advanced Settings</h3>
        <p className="section-description">Additional task configuration options</p>
      </div>
      
      <div className="form-group">
        <label className="form-label">Task Status</label>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => handleInputChange('isActive', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="isActive">
            Active Task
          </label>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInformation();
      case 'timeline':
        return renderTimelineEffort();
      case 'details':
        return renderTaskDetails();
      case 'advanced':
        return renderAdvancedSettings();
      default:
        return renderBasicInformation();
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Create New Task"
        description="Set up a comprehensive task with all necessary details and configurations"
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Main Content */}
        <div className="project-create-container" style={{ width: '100%', maxWidth: '100%' }}>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {/* Tab Navigation */}
          <div className="project-tabs">
            <div className="project-tabs-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="project-tab-icon">
                    {tab.icon}
                  </div>
                  <div className="project-tab-content">
                    <div className="project-tab-label">{tab.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="project-form-card">
            {renderTabContent()}
          </div>

          {/* Form Actions */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/projects/task-management')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Creating Task...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Create Task
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </>
  );
};

export default CreateTask;
